import { formatMoney } from '@theme/utilities';
import { ToggleCrossSellsUpdatedEvent } from '@theme/events';

class ToggleCrossSellList extends HTMLElement {
  constructor() {
    super();

    this.crossSells = null;
  }

  connectedCallback() {
    this.crossSells = this.querySelectorAll('[data-ref="toggle-cross-sell"]');
  }

  getSelectedItems() {
    let items = [];
    let total_price = 0;
    let total_compare_at_price = 0;

    this.crossSells.forEach((crossSell) => {
      const checkbox = crossSell.querySelector('[data-ref="toggle-cross-sell-checkbox"]');
      const button = crossSell.querySelector('[data-ref="toggle-cross-sell-button"]');

      if (checkbox != null && checkbox.checked == true) {
        items.push(buildItem(checkbox));

        total_price += parseInt(crossSell.getPrices().price);
        total_compare_at_price += parseInt(crossSell.getPrices().compareAtPrice);
      }

      if (button != null && button.classList.contains('button--active') == true) {
        items.push(buildItem(button));

        total_price += parseInt(crossSell.getPrices().price);
        total_compare_at_price += parseInt(crossSell.getPrices().compareAtPrice);
      }
    });

    return { items: items, total_price: total_price, total_compare_at_price: total_compare_at_price };
  }
}

/**
 * Construit l'item à ajouter au panier. Si le déclencheur porte un renvoi
 * (data-property-name / data-property-value), il est ajouté en propriété de
 * ligne : la ligne de l'option rappelle ainsi à quel produit elle se rapporte.
 */
function buildItem(trigger) {
  const item = {
    id: trigger.dataset.variantId,
    quantity: 1,
  };

  const propertyName = trigger.dataset.propertyName;
  const propertyValue = trigger.dataset.propertyValue;

  if (propertyName && propertyValue) {
    item.properties = { [propertyName]: propertyValue };
  }

  return item;
}

if (!customElements.get('toggle-cross-sell-list')) {
  customElements.define('toggle-cross-sell-list', ToggleCrossSellList);
}

class ToggleCrossSell extends HTMLElement {
  constructor() {
    super();

    this.checkbox = null;
    this.button = null;
    this.select = null;
    this.image = null;
    this.price = null;
    this.lineProperty = null;
  }

  connectedCallback() {
    this.checkbox = this.querySelector('[data-ref="toggle-cross-sell-checkbox"]');
    this.button = this.querySelector('[data-ref="toggle-cross-sell-button"]');
    this.select = this.querySelector('[data-ref="toggle-cross-sell-select"]');
    this.image = this.querySelector('[data-ref="toggle-cross-sell-image"]');
    this.price = this.querySelector('[data-ref="toggle-cross-sell-price"]');
    this.lineProperty = this.querySelector('[data-ref="toggle-cross-sell-line-property"]');

    this.select?.addEventListener('change', this.variantChanged.bind(this));
    this.button?.addEventListener('click', this.toggleAddButton.bind(this));
    this.checkbox?.addEventListener('change', this.checkboxChanged.bind(this));
    this.addEventListener('click', this.handleCardClick.bind(this));

    this.syncLineProperty();
  }

  disconnectedCallback() {
    this.select?.removeEventListener('change', this.variantChanged.bind(this));
    this.button?.removeEventListener('click', this.toggleAddButton.bind(this));
    this.removeEventListener('click', this.handleCardClick.bind(this));
  }

  /**
   * Active ou désactive la mention posée sur le produit principal.
   * Sans attribut name, le champ est ignoré par le formulaire d'ajout au panier.
   */
  syncLineProperty() {
    if (this.lineProperty == null) return;

    const isActive =
      (this.checkbox != null && this.checkbox.checked == true) ||
      (this.button != null && this.button.classList.contains('button--active') == true);

    if (isActive) {
      this.lineProperty.setAttribute('name', this.lineProperty.dataset.propertyName);
    } else {
      this.lineProperty.removeAttribute('name');
    }
  }

  handleCardClick(event) {
    // Laisse la redirection du lien produit et le changement de variante intacts
    if (event.target.closest('a') != null) return;
    if (event.target.closest('.select-wrapper') != null) return;

    // Les clics directs sur la checkbox ou le bouton sont gérés par leurs propres écouteurs
    const checkboxLabel = this.checkbox?.closest('.checkbox');
    if (checkboxLabel != null && checkboxLabel.contains(event.target)) return;
    if (this.button != null && this.button.contains(event.target)) return;

    if (this.checkbox != null) {
      this.checkbox.checked = !this.checkbox.checked;
      this.checkboxChanged();
    } else if (this.button != null) {
      this.toggleAddButton();
    }
  }

  checkboxChanged() {
    this.syncLineProperty();
    document.dispatchEvent(new ToggleCrossSellsUpdatedEvent());
  }

  toggleAddButton(active) {
    if (this.button.classList.contains('button--active') == false || active == true) {
      this.button.classList.add('button--active');
      this.button.classList.remove('button--secondary');
    } else {
      this.button.classList.remove('button--active');
      this.button.classList.add('button--secondary');
    }

    this.syncLineProperty();
    document.dispatchEvent(new ToggleCrossSellsUpdatedEvent());
  }

  variantChanged() {
    const optionSelected = this.select.options[this.select.selectedIndex];

    this.updateProductPrice(optionSelected);
    this.updateProductImage(optionSelected);
    this.updateVariantId(optionSelected);

    if (this.checkbox != null) this.checkbox.checked = true;
    if (this.button != null) this.toggleAddButton(true);

    this.syncLineProperty();
    document.dispatchEvent(new ToggleCrossSellsUpdatedEvent());
  }

  updateProductPrice(optionSelected) {
    const variantPrice = optionSelected.dataset.price;
    const variantCompareAtPrice = optionSelected.dataset.compareAtPrice;

    this.price.querySelector('.price').innerHTML = formatMoney(variantPrice);
    this.dataset.price = variantPrice;

    if (variantCompareAtPrice != null) {
      this.price.querySelector('.compare-at-price').innerHTML = formatMoney(variantCompareAtPrice);
      this.dataset.compareAtPrice = variantCompareAtPrice;
    }
  }

  updateProductImage(optionSelected) {
    const variantImage = optionSelected.dataset.variantImage;

    if (variantImage != null && this.image != null) {
      this.image.querySelector('img').src = variantImage;
    }
  }

  updateVariantId(optionSelected) {
    const variantId = optionSelected.value;

    if (this.checkbox != null) this.checkbox.dataset.variantId = variantId;
    if (this.button != null) this.button.dataset.variantId = variantId;
  }

  getPrices() {
    let price = 0;
    let compareAtPrice = 0;

    if (this.dataset.price != null) {
      price = parseInt(this.dataset.price);
    }

    if (this.dataset.compareAtPrice != null) {
      compareAtPrice = parseInt(this.dataset.compareAtPrice);
    } else {
      compareAtPrice = parseInt(this.dataset.price);
    }

    return {
      price: price,
      compareAtPrice: compareAtPrice,
    };
  }
}

if (!customElements.get('toggle-cross-sell')) {
  customElements.define('toggle-cross-sell', ToggleCrossSell);
}
